import { css, html, nothing, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../ui/base/BaseElement.js";
import { observeContext } from "./SugarConfiguratorContext.js";
import type { ProformaProduct } from "./SugarProforma.js";
import { ConfiguratorModal } from "./ConfiguratorModal.js";
import("./SugarProforma.js");
import("./ConfiguratorModal.js");
import { t } from "../translation/i18n";
import { resolveSceneModulesWithFeatures } from "./ConfiguratorData.js";
import { ModulePriceCalculator } from "./ModulePriceCalculator.js";
import i18next from "i18next";
import { logger } from "../util/Logger.js";
const styles = css`
  .bottom-panel {
    width: 97%;
    background: transparent;
    border-top: none;
    padding: 16px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .left-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toolbar-btn {
    border: none;
    background: transparent;
    padding: 12px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 60px;
    font-size: 10px;
    font-weight: 500;
    color: #666;
    border-radius: 8px;
  }

  .toolbar-btn svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    fill: white;
  }

  .right-section {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .price-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .price {
    font-size: 24px;
    font-weight: 700;
    color: #333;
  }

  .currency {
    font-size: 16px;
    font-weight: 500;
    color: #666;
  }

  .product-list-btn {
    background: #325a82;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: 768px) {
    .bottom-panel {
      flex-direction: column;
      gap: 16px;
      text-align: center;
      padding: 12px 16px;
    }

    .left-section {
      order: 2;
      gap: 6px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .right-section {
      order: 1;
      flex-direction: column;
      gap: 12px;
    }

    .toolbar-btn {
      min-width: 48px;
      padding: 8px;
      font-size: 9px;
    }

    .toolbar-btn svg {
      width: 16px;
      height: 16px;
    }
  }
`;

@customElement("sugar-configurator-operations")
@observeContext("model", "refresh", "scene")
export class SugarConfiguratorOperations extends BaseElement {
  static styles = [styles];
  protected useShadowRoot = false;

  private _calculator = new ModulePriceCalculator();

  constructor() {
    super();
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang");
    if (lang === "en" || lang === "de" || lang === "tr") {
      i18next.changeLanguage(lang);
    } else {
      i18next.changeLanguage("tr");
    }
  }

  @property({ type: Boolean }) 
  showDimensions = true;
  
  @property({ type: Boolean }) 
  showClear = true;
  
  @property({ type: Boolean }) 
  showTopView = true;
  
  @property({ type: Boolean }) 
  showProductList = true;
  
  @property({ type: Boolean }) 
  showPrice = true;

  @property({type: Boolean})
  showCreateRender = true;

  @state()
  private _showProforma = false;

  @state()
  private _totalPrice = 0;

  @state()
  private _apiProducts: ProformaProduct[] = [];

  @state()
  private _isLoading = false;

  private _screenshot: unknown;

  private _onI18nLanguageChanged = () => {
    this.refreshSceneModules();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    i18next.on("languageChanged", this._onI18nLanguageChanged);
  }

  override disconnectedCallback(): void {
    i18next.off("languageChanged", this._onI18nLanguageChanged);
    super.disconnectedCallback();
  }

  private refreshSceneModules() {
    const products = (this as any).context?.products as unknown[] | undefined;
    const modules = resolveSceneModulesWithFeatures(products);
  }

  onContextChanged() {
    this.refreshSceneModules();
  }

  async fetchProductPrices() {
    if (this._isLoading) return;

    const sceneModels = (this as any).context?.products as unknown[][] | undefined;
    if (!sceneModels?.length) {
      this._apiProducts = [];
      this._totalPrice = 0;
      this.requestUpdate();
      return;
    }

    this._isLoading = true;
    this.requestUpdate();

    try {
      const { products, totalPrice } = await this._calculator.calculate(sceneModels);
      this._apiProducts = products;
      this._totalPrice = totalPrice;
    } catch (error) {
      logger.error("Fiyat bilgisi alınırken hata oluştu:", error);
      this._apiProducts = [];
      this._totalPrice = 0;
    } finally {
      this._isLoading = false;
      this.requestUpdate();
    }
  }

  onRefreshChanged() {}

  onSceneChanged() {
    this.refreshSceneModules();
    this.fetchProductPrices();
  }

  async handleProductListClick() {
    const validation = (this as any).context?.validateConfigurations();
    if (!validation) {
      const modal = document.createElement(
        "configurator-modal2"
      ) as ConfiguratorModal;
      modal.title = "Uyarı";
      modal.innerHTML =
        "<div>Koltuğunuzu tamamlamak için lütfen köşe modülünüzün yanına bir modül ekleyiniz.</div>";
      this.appendChild(modal);
      return;
    }
    this._showProforma = true;
    await (this as any).context?.setTopViewForRender();
    this._screenshot = await (this as any).context?.takeScreenshot();
    this.requestUpdate();
  }

  handleCloseProforma() {
    this._showProforma = false;
    this.requestUpdate();
  }

  handleDimensionsClick() {
    (this as any).context?.showDimensions();
  }

  handleClearClick() {
    (this as any).context?.clearAll();
  }

  handleUndoClick() {
    // TODO: Geri alma işlevi eklenecek
  }

  handleTopViewClick() {
    (this as any).context?.showTopView();
  }

  async handleCreateRender() {
    const url = await (this as any).context?.takeScreenshot();
    if (!url) return;

    const baseName = document.title?.trim() || "render";
    const safeName = baseName.replace(/[^\w\-]+/g, "_");

    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  renderBottomPanel(): TemplateResult {
    return html`
      <div class="bottom-panel">
        <div class="left-section">
          ${this.showDimensions ? html`
          <button class="toolbar-btn" @click=${this.handleDimensionsClick}>
            <svg>
              <use href="#dimensions-icon"></use>
            </svg>
            <span>${t("dimensions")}</span>
          </button>` : nothing}
          ${this.showClear ? html`
          <button class="toolbar-btn" @click=${this.handleClearClick}>
            <svg>
              <use href="#clear-icon"></use>
            </svg>
            <span>${t("resetArea")}</span>
          </button>` : nothing}
          ${this.showTopView ? html`
          <button class="toolbar-btn" @click=${this.handleTopViewClick}>
            <svg>
              <use href="#top-view-icon"></use>
            </svg>
            <span>${t("topView")}</span>
          </button>` : nothing}
          ${this.showCreateRender ? html`
          <button class="toolbar-btn" @click=${this.handleCreateRender}>
            <svg>
              <use href="#top-view-icon"></use>
            </svg>
            <span>${t("createRender")}</span>
          </button>` : nothing}
          <slot name="operations-extra"></slot>
        </div>
        <div class="spacer"></div>
        <div class="right-section">
          ${this.showPrice ? html`
          <div class="price-section">
            ${this._isLoading
              ? html`<span class="price">${t("calculating")}</span>`
              : html`
                  <span class="price"
                    >${new Intl.NumberFormat("tr-TR").format(
                      this._totalPrice
                    )}</span
                  >
                  <span class="currency">,00 ${i18next.t("__currency__")}</span>
                `}
          </div>` : nothing}
          ${this.showProductList ? html`
          <button
            class="product-list-btn"
            @click=${this.handleProductListClick}
            ?disabled=${this._isLoading || this._apiProducts.length === 0}
          >
            ${this._isLoading ? t("loading") : t("productList")}
            <svg width="8" height="14" viewBox="0 0 8 14" fill="transparent">
              <use href="#chevron-right-icon"></use>
            </svg>
          </button>` : nothing}
        </div>
        <style>
          .bottom-panel {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            gap: 16px;
            margin-bottom: 8px;
          }
          .left-section {
            display: flex;
            flex-direction: row;
            gap: 12px;
            align-items: center;
          }
          .spacer {
            flex: 1;
          }
          .right-section {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 12px;
          }
          .price-section {
            font-size: 20px;
            font-weight: 600;
            margin-right: 8px;
          }
        </style>
      </div>
    `;
  }

  protected renderHtml(): TemplateResult {
    return html`
      ${this.renderBottomPanel()}
      <sugar-proforma
        .visible=${this._showProforma}
        .productImageUrl=${this._screenshot}
        .products=${this._apiProducts}
        .totalPrice=${this._totalPrice}
        @close-proforma=${this.handleCloseProforma}
      ></sugar-proforma>
    `;
  }
}
